// Crear archivo: src/components/modules/ventas/DevolucionParcialDialog.js

import React, { useState, useEffect } from 'react';
import { FaTimes, FaUndo, FaMinus, FaPlus } from 'react-icons/fa';
import Button from '../../common/Button';
import { toast } from 'react-toastify';

const DevolucionParcialDialog = ({ isOpen, onClose, venta, onDevolucion }) => {
  const [productosDevolucion, setProductosDevolucion] = useState([]);
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    if (venta && venta.detalles) {
      setProductosDevolucion(
        venta.detalles.map(detalle => ({
          id: detalle.id,
          producto_id: detalle.producto_id,
          nombre: detalle.producto_info?.nombre || detalle.nombre || 'Sin nombre',
          precio_unitario: detalle.precio_unitario,
          cantidad_original: detalle.cantidad,
          cantidad_devolver: 0,
          subtotal: 0
        }))
      );
    }
  }, [venta]);

  const handleCantidadChange = (index, nuevaCantidad) => {
    const cantidad = Math.max(0, Math.min(nuevaCantidad, productosDevolucion[index].cantidad_original));
    
    const nuevosProductos = [...productosDevolucion];
    nuevosProductos[index].cantidad_devolver = cantidad;
    nuevosProductos[index].subtotal = cantidad * nuevosProductos[index].precio_unitario;
    
    setProductosDevolucion(nuevosProductos);
  };

  const getTotalDevolucion = () => {
    return productosDevolucion.reduce((total, prod) => total + prod.subtotal, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const productosADevolver = productosDevolucion.filter(p => p.cantidad_devolver > 0);
    
    if (productosADevolver.length === 0) {
      toast.warning('Debe seleccionar al menos un producto para devolver');
      return;
    }

    if (!motivo.trim()) {
      toast.warning('Por favor ingrese el motivo de la devolución');
      return;
    }

    setProcesando(true);
    
    try {
      await onDevolucion(productosADevolver, motivo);
      onClose();
      toast.success('Devolución parcial procesada correctamente');
    } catch (error) {
      console.error('Error al procesar devolución:', error);
      toast.error('Error al procesar la devolución');
    } finally {
      setProcesando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-e0e3-surface text-e0e3-foreground shadow-xl">
        <div className="border-b border-white/10 p-6">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center text-xl font-semibold text-white">
              <FaUndo className="mr-2 text-amber-300" />
              Devolución Parcial - Venta {venta?.numero}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-e0e3-muted hover:bg-white/10 hover:text-white"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="max-h-[60vh] flex-1 overflow-y-auto p-6">
            <div className="mb-6 space-y-4">
              <h3 className="font-medium text-e0e3-muted">Seleccione productos a devolver:</h3>
              
              {productosDevolucion.map((producto, index) => (
                <div key={producto.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.06]">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white">{producto.nombre}</p>
                      <p className="text-sm text-e0e3-muted">
                        Precio unitario: ${producto.precio_unitario.toFixed(2)} | 
                        Cantidad vendida: {producto.cantidad_original}
                      </p>
                    </div>
                    
                    <div className="flex shrink-0 items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleCantidadChange(index, producto.cantidad_devolver - 1)}
                          className="rounded bg-white/10 p-1 hover:bg-white/15 disabled:opacity-40"
                          disabled={producto.cantidad_devolver === 0}
                        >
                          <FaMinus size={12} />
                        </button>
                        
                        <input
                          type="number"
                          value={producto.cantidad_devolver}
                          onChange={(e) => handleCantidadChange(index, parseInt(e.target.value, 10) || 0)}
                          className="nexo-field w-16 rounded px-2 py-1 text-center"
                          min="0"
                          max={producto.cantidad_original}
                        />
                        
                        <button
                          type="button"
                          onClick={() => handleCantidadChange(index, producto.cantidad_devolver + 1)}
                          className="rounded bg-white/10 p-1 hover:bg-white/15 disabled:opacity-40"
                          disabled={producto.cantidad_devolver >= producto.cantidad_original}
                        >
                          <FaPlus size={12} />
                        </button>
                      </div>
                      
                      <div className="min-w-[100px] text-right">
                        <p className="font-semibold text-white">
                          ${producto.subtotal.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-e0e3-muted">
                Motivo de la devolución *
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                className="nexo-field w-full rounded-md px-3 py-2"
                rows="3"
                placeholder="Ingrese el motivo de la devolución..."
                required
              />
            </div>

            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="flex items-center justify-between text-lg font-medium">
                <span className="text-e0e3-muted">Total a devolver:</span>
                <span className="text-amber-300">${getTotalDevolucion().toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 bg-black/20 p-6">
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                color="secondary"
                onClick={onClose}
                disabled={procesando}
              >
                Cancelar
              </Button>
              
              <Button
                type="submit"
                color="warning"
                icon={<FaUndo />}
                loading={procesando}
                disabled={procesando || getTotalDevolucion() === 0}
              >
                Procesar Devolución
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DevolucionParcialDialog;
